import { valibotResolver } from "@hookform/resolvers/valibot";
import { Link as LinkIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import usePasteUrlTargeting from "../../../../datahooks/usePasteUrlTargeting";
import { UrlTargetingSchema } from "../../../../schemas";
import { useSearchParamsContext } from "./SearchParamsContext";

export default function PasteUrlTargeting({
  hasSearchParams,
}: {
  hasSearchParams: boolean;
}) {
  const isDesktop = useTwBreakpoint("lg");

  const { setSearchParams } = useSearchParamsContext();

  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const { getSearchParamsFromUrl, isGettingSearchParamsFromUrl } =
    usePasteUrlTargeting();

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      url: "",
    },
    resolver: valibotResolver(UrlTargetingSchema),
  });

  function resetState() {
    reset();
    setStepIndex(0);
  }

  function onNext({ url }: { url: string }) {
    if (hasSearchParams && stepIndex === 0) {
      setStepIndex(1);
    } else {
      getSearchParamsFromUrl({ url }).then((result) => {
        setSearchParams(result);
        setIsOpen(false);
        resetState();
      });
    }
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Close = isDesktop ? DialogClose : DrawerClose;
  const Title = isDesktop ? DialogTitle : DrawerTitle;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={resetState}>
      <Trigger asChild>
        <Button size="sm" leftIcon={<LinkIcon />} variant="outline-black">
          Search by URL
        </Button>
      </Trigger>
      <Content className="lg:w-[448px]">
        <Title className="mb-2">
          {stepIndex === 0
            ? "Enter URL of audience from LinkedIn"
            : "Overwrite targeting?"}
        </Title>

        {stepIndex === 0 ? (
          <FormField
            size="lg"
            htmlFor="search-url"
            label="URL"
            className="mb-8 mt-6"
            hint="Applying URL over a set targeting will overwrite the settings. This cannot be undone"
            error={errors.url?.message}
          >
            <Input
              id="search-url"
              variant="lg"
              placeholder="Enter URL here..."
              {...register("url")}
            />
          </FormField>
        ) : (
          <p className="mb-8 text-center text-black-500">
            This will overwrite any settings in your targeting. This cannot be
            undone. Do you wish to proceed?
          </p>
        )}

        <div className="flex justify-between gap-x-2.5">
          <Close asChild>
            <Button
              className="lg:flex-1"
              variant="secondary-black"
              onClick={() => setIsOpen(false)}
              disabled={isGettingSearchParamsFromUrl}
            >
              Cancel
            </Button>
          </Close>

          <Button
            className="lg:flex-1"
            onClick={handleSubmit(onNext)}
            isLoading={isGettingSearchParamsFromUrl}
          >
            {stepIndex === 0 ? "Next" : "Apply targeting"}
          </Button>
        </div>
      </Content>
    </Component>
  );
}
